/**
 * Inline pet↔vet linking panel — rendered on the pet card in CustomerDetail.
 *
 * Shows linked vets (primary marked with a star). Lets managers/owners link,
 * unlink, and set primary. Staff see the list read-only.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import {
  linkVetToPet,
  listVets,
  listVetsForPet,
  setPrimaryVetForPet,
  unlinkVetFromPet,
} from '@/lib/api';
import type { PetVetLink, Vet } from '@/lib/types';
import { Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface PetVetsPanelProps {
  petId: string;
}

export function PetVetsPanel({ petId }: PetVetsPanelProps) {
  const { state } = useAuthContext();
  const editable = state.me?.capabilities?.includes('pets.configure') ?? false;

  const [links, setLinks] = useState<PetVetLink[]>([]);
  const [allVets, setAllVets] = useState<Vet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVetId, setSelectedVetId] = useState('');
  const [linking, setLinking] = useState(false);

  async function refresh() {
    try {
      const [l, v] = await Promise.all([listVetsForPet(petId), listVets()]);
      setLinks(l);
      setAllVets(v);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId]);

  const linkedVetIds = new Set(links.map((l) => l.vetId));
  const availableVets = allVets.filter((v) => !linkedVetIds.has(v.id));

  async function handleLink() {
    if (!selectedVetId) return;
    setLinking(true);
    try {
      await linkVetToPet(petId, selectedVetId);
      setSelectedVetId('');
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLinking(false);
    }
  }

  async function handleUnlink(vetId: string) {
    try {
      await unlinkVetFromPet(petId, vetId);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleSetPrimary(vetId: string) {
    try {
      await setPrimaryVetForPet(petId, vetId);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (loading) return null;

  return (
    <div className="mt-3 border-t border-border pt-3">
      <p className="mb-2 text-xs font-medium text-muted-foreground">Vets</p>
      {error && <p className="mb-1 text-xs text-red-600">{error}</p>}
      {links.length === 0 ? (
        <p className="text-xs text-muted-foreground">No vets linked.</p>
      ) : (
        <div className="space-y-1">
          {links.map((link) => {
            const vet = link.vet;
            return (
              <div
                key={link.vetId}
                className="flex items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1.5"
              >
                <div className="min-w-0">
                  <span className="text-xs font-medium">{vet?.practiceName ?? link.vetId}</span>
                  {vet?.vetName && (
                    <span className="ml-1 text-xs text-muted-foreground">{vet.vetName}</span>
                  )}
                  {vet?.phone && (
                    <span className="ml-2 text-xs text-muted-foreground">{vet.phone}</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {link.isPrimary === 1 ? (
                    <Star
                      className="h-3 w-3 fill-yellow-400 text-yellow-400"
                      aria-label="Primary vet"
                    />
                  ) : (
                    editable && (
                      <button
                        type="button"
                        aria-label="Set as primary"
                        onClick={() => handleSetPrimary(link.vetId)}
                        className="rounded p-0.5 text-muted-foreground hover:text-yellow-500"
                        title="Set as primary"
                      >
                        <Star className="h-3 w-3" />
                      </button>
                    )
                  )}
                  {editable && (
                    <button
                      type="button"
                      aria-label="Unlink vet"
                      onClick={() => handleUnlink(link.vetId)}
                      className="rounded p-0.5 text-muted-foreground hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editable && availableVets.length > 0 && (
        <div className="mt-2 flex gap-2">
          <select
            value={selectedVetId}
            onChange={(e) => setSelectedVetId(e.target.value)}
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">Link a vet…</option>
            {availableVets.map((v) => (
              <option key={v.id} value={v.id}>
                {v.practiceName}
                {v.vetName ? ` — ${v.vetName}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!selectedVetId || linking}
            onClick={handleLink}
            className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            Link
          </button>
        </div>
      )}
    </div>
  );
}
